<template>
  <div v-if="slots && slots.length > 0" class="next-available-slots mt-4">
    <h5 class="next-available-title">{{ $t("experiences.nextAvailableDates") }}</h5>
    <div class="next-slots-grid">
      <div
        v-for="slot in slots"
        :key="slot.id"
        class="next-slot-card"
        @click="$emit('select', slot)"
      >
        <div class="next-slot-header">
          <div class="next-slot-date">
            <svg class="date-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="3" width="12" height="11" rx="2" stroke="currentColor" stroke-width="1.5"/>
              <path d="M2 6H14" stroke="currentColor" stroke-width="1.5"/>
              <path d="M5 2V4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M11 2V4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <strong>{{ formatDate(slot.date) }}</strong>
          </div>
          <div class="next-slot-time">
            <svg class="time-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.5"/>
              <path d="M8 4V8L10 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <strong>{{ formatTime(slot.startTime) }}</strong>
          </div>
        </div>

        <div class="next-slot-details">
          <div class="next-slot-detail-item">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 8C9.10457 8 10 7.10457 10 6C10 4.89543 9.10457 4 8 4C6.89543 4 6 4.89543 6 6C6 7.10457 6.89543 8 8 8Z" fill="currentColor"/>
              <path d="M8 9C6.34315 9 5 10.3431 5 12V13H11V12C11 10.3431 9.65685 9 8 9Z" fill="currentColor"/>
            </svg>
            <span>{{ slot.minPeople }}-{{ slot.maxPeople }} {{ $t("experiences.maxPeople") }}</span>
          </div>
          <div v-if="slot.guide && slot.guide.name" class="next-slot-detail-item">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0ZM8 2C11.3137 2 14 4.68629 14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2Z" fill="currentColor"/>
              <path d="M8 4C6.89543 4 6 4.89543 6 6C6 7.10457 6.89543 8 8 8C9.10457 8 10 7.10457 10 6C10 4.89543 9.10457 4 8 4Z" fill="currentColor"/>
              <path d="M8 9C6.34315 9 5 10.3431 5 12V13H11V12C11 10.3431 9.65685 9 8 9Z" fill="currentColor"/>
            </svg>
            <span><strong>{{ $t("experiences.guide") }}:</strong> {{ slot.guide.name }}</span>
          </div>
        </div>

        <div v-if="showPrice && bracketPricesOf(slot).length" class="next-slot-detail-item">
          <span v-for="(bracket, i) in bracketPricesOf(slot)" :key="bracket.key">
            <template v-if="i > 0"> · </template>{{ $t(`reservation.ageBrackets.${bracket.key.toLowerCase()}`) }}
            <strong>{{ bracket.price > 0 ? formatPrice(bracket.price) : $t('experiences.free') }}</strong>
          </span>
        </div>
        <div v-else-if="showPrice && slot.displayPrice > 0" class="next-slot-detail-item">
          <strong>{{ formatPrice(slot.displayPrice) }}</strong>
        </div>

        <button class="next-slot-button">
          {{ $t("experiences.select") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { parseDateString } from '@/services/slotAvailability.service.js';
import { pricedAgeBrackets, formatEuro } from '@/helpers/agePrices.js';

export default {
  name: 'NextAvailableSlots',
  props: {
    slots: {
      type: Array,
      required: true
    },
    showPrice: {
      type: Boolean,
      default: false
    },
    priceType: {
      type: String,
      default: 'PerExperience'
    }
  },
  methods: {
    bracketPricesOf(slot) {
      return this.priceType === 'PerPerson' ? pricedAgeBrackets(slot.ageBracketPrices) : [];
    },
    formatPrice(value) {
      return formatEuro(value, this.$i18n.locale);
    },
    formatDate(date) {
      if (!date) return '';
      const dateObj = parseDateString(date);

      if (dateObj && !isNaN(dateObj.getTime())) {
        return dateObj.toLocaleDateString(this.$i18n.locale, {
          weekday: 'short',
          day: 'numeric',
          month: 'short'
        });
      }
      return date;
    },
    formatTime(time) {
      if (!time) return '';
      return time.substring(0, 5);
    }
  }
};
</script>

<style scoped>
.next-available-slots {
  margin-top: 2rem;
  text-align: left;
}

.next-available-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--secondary-color);
  margin: 0 0 1.5rem 0;
  text-align: center;
}

.next-slots-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1.5rem;
}

.next-slot-card {
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.next-slot-card:hover {
  border-color: var(--primary-color);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.next-slot-header {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.next-slot-date,
.next-slot-time {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--secondary-color);
  font-size: 0.95rem;
}

.next-slot-date strong,
.next-slot-time strong {
  font-weight: 600;
}

.date-icon,
.time-icon {
  color: var(--primary-color);
  flex-shrink: 0;
}

.next-slot-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f3f4f6;
}

.next-slot-detail-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
}

.next-slot-detail-item svg {
  color: var(--primary-color);
  flex-shrink: 0;
}

.next-slot-button {
  background: var(--primary-color);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
}

.next-slot-button:hover {
  background: #8fb87a;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

@media (max-width: 768px) {
  .next-slots-grid {
    grid-template-columns: 1fr;
  }
}
</style>
